import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { useYouTubeAccountReset } from '@/lib/youtubeConnection';
import { formatCount } from '@/lib/channel';
import { VideoThumb } from '@/components/channel/VideoThumb';

export function GeneralTrendsBrief() {
  const [data, setData] = useState(null);
  const [peers, setPeers] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: payload }, peerResult] = await Promise.all([
          api.get('/channel/trends', { params: { region: 'IN' } }),
          api.get('/channel/peers').catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setData(payload);
          setPeers(peerResult?.data || null);
        }
      } catch {
        if (!cancelled) setData({ connected: false, top: [], by_genre: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const clearChannel = useCallback(() => {
    setPeers(null);
    setData({ connected: false, top: [], by_genre: [] });
  }, []);
  useYouTubeAccountReset(clearChannel);

  const top = (data?.top || [])[0];
  const genres = (data?.by_genre || []).filter((row) => (row.videos || []).length).slice(0, 3);

  return (
    <section id="general-trends" className="group rounded-[18px] border-0 bg-gradient-to-br from-[#1a4d48] via-[#1a2c32] to-[#141a24] px-5 py-5 sm:px-6 sm:py-6 space-y-4 font-jakarta transition-colors duration-200 hover:from-[#22645c] hover:via-[#203840] hover:to-[#1a2230]">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1.5">
          <h2 className="text-base font-bold text-[#d7dee8] tracking-tight origin-left transition-all duration-200 group-hover:scale-[1.06] group-hover:text-white">General Trends</h2>
          <p className="text-sm text-[#8b95a7] leading-5">
            Trending videos, plus channels and videos like yours.
          </p>
        </div>
        <Link
          to="/channel/trends"
          className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#34d399] hover:text-white"
        >
          Open <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {!data ? (
        <p className="text-sm text-[#7d8799]">Loading trends…</p>
      ) : null}

      {data && !data.connected ? (
        <p className="text-sm text-[#9aa6b8]">Connect YouTube to load trending videos for your genres.</p>
      ) : null}

      {data?.connected && ((peers?.channels || []).length || (peers?.videos || []).length) ? (
        <div className="space-y-2">
          {(peers.channels || []).length ? (
            <p className="text-sm text-[#d7dee8]">
              <span className="block text-[10px] uppercase tracking-wide text-[#7d8799]">Channels like yours</span>
              {peers.channels.slice(0, 3).map((channel) => channel.title).join(' · ')}
            </p>
          ) : null}
          {(peers.videos || []).length ? (
            <p className="text-sm text-[#d7dee8]">
              <span className="block text-[10px] uppercase tracking-wide text-[#7d8799]">Videos like yours</span>
              {peers.videos.slice(0, 3).map((video) => video.title).join(' · ')}
            </p>
          ) : null}
          {peers.updates?.[0] ? <p className="text-sm text-[#d7dee8]">{peers.updates[0]}</p> : null}
          {(peers.phrases || []).length ? (
            <p className="text-xs text-[#8b95a7]">Try these words: {peers.phrases.join(', ')}</p>
          ) : null}
          {peers.strategy ? <p className="text-sm text-white">{peers.strategy}</p> : null}
        </div>
      ) : null}

      {data?.connected && (top || genres.length > 0) ? (
        <ul className="space-y-2">
          {top ? (
              <li className="flex items-center gap-3 rounded-xl border-0 bg-white/[0.05] px-3 py-2.5 min-w-0">
              <VideoThumb video={top} className="h-14 w-24 rounded-lg" />
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] uppercase tracking-wide text-[#7d8799]">Top 10 on YouTube</span>
                <span className="block text-sm font-medium text-white truncate mt-1">{top.title}</span>
                <span className="block text-xs text-[#8b95a7] mt-1">{formatCount(top.views)} views</span>
              </span>
            </li>
          ) : null}
          {genres.map((genre) => {
            const video = genre.videos[0];
            return (
              <li key={genre.category_id} className="flex items-center gap-3 rounded-xl border-0 bg-white/[0.05] px-3 py-2.5 min-w-0">
                <VideoThumb video={video} className="h-14 w-24 rounded-lg" />
                <span className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase tracking-wide text-[#2DD4BF]">{genre.label}</span>
                  <span className="block text-sm font-medium text-white truncate mt-1">{video.title}</span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
